import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { debounceTime, map, mergeMap, startWith } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import * as ModalActions from '../../modal/actions/modal.action';
import * as LocaleActions from '../../locale/actions/locale.action';
import * as UIActions from '../actions/ui.action';
import * as DeviceActions from '../actions/device.action';
import * as UserActions from '../../user/actions/user.action';
import { LocaleService } from '../../../platform/localization/locale/locale.service';
import { ModalService } from '../../modal/services/modal.service';
import { DeviceState } from '../states/device.state';
import { UIState } from '../states/ui.state';
import { DeviceService } from '../services/device.service';



const defaultLanguage = environment.defaultLanguage;

@Injectable()
export class UIEffects {
    localeSet$: Observable<Action> = createEffect(() => this.actions$
      .pipe(
        ofType(LocaleActions.set),
        mergeMap((action) => {
          this.localeService.locale = action.locale;
          this.translateService.use(action.locale);
          return [
            UIActions.changed({ locale: action.locale }),
            UserActions.changeLanguage({ language: action.locale }),
          ];
        })));


    modalOpen$: Observable<Action> = createEffect(() => this.actions$
      .pipe(
        ofType(ModalActions.open),
        map((action) => {
          const details = this.modal.open(action);
          return ModalActions.opened({
            open: true,
            cmpType: details.cmpType,
            title: details.trackTitle,
            latestResult: null // reset when opening
          });
        })));

    modalOpened$: Observable<Action> = createEffect(() => this.actions$.pipe(
      ofType(ModalActions.opened),
      map((action) =>
        UIActions.changed({
          modal: action,
        }),
      )));

    modalClose$: Observable<Action> = createEffect(() => this.actions$
      .pipe(
        ofType(ModalActions.close),
        map((action) => {
          const closeResult = this.modal.close(action);

          return ModalActions.closed({
            state: {
              open: false,
              cmpType: null,
              title: null,
              // keep null values to be consistent (instead of undefined)
              latestResult: typeof closeResult === 'undefined' ? null : closeResult,
            }
          });
        })));

    modalClosed$: Observable<Action> = createEffect(() => this.actions$.pipe(
      ofType(ModalActions.closed),
      map((action) =>
        UIActions.changed({
          modal: action.state,
        }),
      )));

    // Any startWith observables - Should always BE LAST!
    localeInit$: Observable<Action> = createEffect(() => this.actions$
      .pipe(
        ofType(LocaleActions.init),
        startWith(LocaleActions.init()),
        map(() => {
          this.translateService.setDefaultLang(defaultLanguage);
          return LocaleActions.set({ locale: this.localeService.locale });
        })));

    navigatorDeviceChange$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.navigatorDevice),
        map((action) => {
            const devicePayload: DeviceState = {
              userAgent: action.userAgent
            };
            const UIpayload: UIState = {
                device: devicePayload
            };

            return UIActions.changed(UIpayload);
        })));

    deviceResolutionChange$: Observable<Action> = createEffect(() => this.actions$.pipe(
      ofType(DeviceActions.deviceResolution),
      debounceTime(300),
      map((action) => {
          const devicePayload: DeviceState = {
            calculatedDeviceType: null,
            screenW: 0,
            screenH: 0
          };

          if (Array.isArray(action.resolution)) {
            const [screenWidth, screenHeight] = action.resolution as [number, number];
            const deviceType = this.deviceService.getDeviceTypeByScreenRes(screenWidth, screenHeight);
            devicePayload.calculatedDeviceType = deviceType;
            devicePayload.screenW = screenWidth;
            devicePayload.screenH = screenHeight;
          }

          const UIpayload: UIState = {
              device: devicePayload
          };

         return UIActions.changed(UIpayload);
      }
      )));

    constructor(
        private actions$: Actions,
        private localeService: LocaleService,
        private translateService: TranslateService,
        private modal: ModalService,
        private deviceService: DeviceService
    ) { }

}
